<template>
    <!-- add button -->
    <div class="p-col-12 p-md-11 p-mx-auto p-mt-2">
        <div class="p-text-right">
            <Button
                label="Add New Cronjob"
                @click="isNewCronjobFormVisible = !isNewCronjobFormVisible"
                class="p-button-sm p-button-text p-button-outlined p-button-info p-text-right" /> 
        </div>
    </div>

    <!-- insert cronjob form -->
    <transition name="expand">
        <div v-show="isNewCronjobFormVisible" class="p-grid p-jc-center p-p-4">
            <div class="card p-col-12 p-md-9 p-mx-auto p-p-4">
                <div class="p-grid">
                    <div class="p-col-12 p-md-4 p-mt-2">
                        <span class="p-float-label">
                            <InputText class="p-col-12" id="function_name" type="text" v-model="newCronjobForm.function_name" />
                            <label for="function_name">Function Name</label>
                        </span>
                    </div>
                    <div class="p-col-12 p-md-4">
                        <span class="p-float-label">
                            <InputNumber class="p-col-12" id="interval_in_minutes" v-model="newCronjobForm.interval_in_minutes" mode="decimal" :useGrouping="false" />
                            <label for="interval_in_minutes">Interval (Minutes)</label>
                        </span>
                    </div>
                    <div class="p-col-12 p-md-4 p-p-3">
                        <span class="p-float-label">
                            <Textarea class="p-col-12" id="comment" type="text" rows="2" :autoResize="true" v-model="newCronjobForm.comment" />
                            <label for="comment">Comment</label>
                        </span>
                    </div>
                    <div class="p-col-12 p-md-6 p-mt-1">
                        <div class="p-d-flex p-text-left">
                            <i class="fa fa-info p-mr-3"></i> 60 - 1 hour
                            <br /> 180 - 3 hours
                            <br /> 360 - 6 hours
                            <br /> 720 - 12 hours
                            <br /> 1440 - 1 day
                            <br /> 2880 - 2 days
                            <br /> 4320 - 3 days
                            <br /> 7200 - 5 days
                            <br /> 10080 - 1 week
                        </div>
                    </div>
                </div>
                <div class="p-grid p-jc-end p-m-0 p-pt-2">
                    <Button @click="addCronjob()" label="Insert Cronjob" class="p-button p-button-info" />
                </div>
            </div>
        </div>
    </transition>

    <!-- cronjob datatable -->
    <div class="p-grid">
        <div class="p-col-12 p-md-11 p-mx-auto p-mt-4">
            <DataTable v-show="cronjobList !== null && cronjobList.length > 0" :paginator="false" :rowHover="true"
                :loading="isItemListLoading"
                :value="cronjobList" editMode="row" dataKey="id"
                v-model:selection="selectedCronjobs"
                responsiveLayout="scroll" >
                <template #loading>
                    Getting cronjobs. Please wait... <br />
                    <ProgressBar mode="indeterminate" style="height: .2em" />
                </template>
                <template #empty>
                    No cronjob found.
                </template>
                <template #footer>
                    <div class="p-grid p-jc-between">
                        <div class="p-col-3 p-text-left">
                            <Button class="p-button-sm p-button-text p-button-info" label="Force Run"
                                @click="() => { this.selectionMode = (this.selectionMode === undefined || this.selectionMode === null ? 'multiple' : null) }" />
                        </div>
                        <div class="p-col-3 p-text-right">
                            <Button class="p-button-outlined p-button-raised p-button-success" label="Execute Selected Cronjobs"
                                :disabled="selectedCronjobs === null || selectedCronjobs.length === 0"
                                v-show="selectedCronjobs !== undefined && selectedCronjobs !== null && selectedCronjobs.length > 0"
                                :loading="executingSelectedCronjobs"
                                @click="executeSelectedCronjobs()"/>
                        </div>
                    </div>
                </template>
                <Column :selectionMode="selectionMode" headerStyle="width: %5"></Column>
                <Column field="function_name" header="Function Name" sortable style="width:15%">
                    <template #body="{data}">
                        <Tag :value="data.function_name" :class="data.enabled ? 'tag-enabled' : 'tag-disabled'" />
                        <br/>
                        <Tag value="IS RUNNING NOW" v-if="data.is_running" class="p-mt-2" severity="danger" />
                    </template>
                    <template #editor="{}"></template>
                </Column>
                <Column field="interval_in_minutes" header="Interval Minutes" style="width:5%">
                    <template #body="{data}">
                        {{data.interval_in_minutes}}
                    </template>
                    <template #editor="{data, field}">
                        <InputNumber id="interval_in_minutes" v-model="data[field]" mode="decimal" :useGrouping="false" />
                    </template>
                </Column>
                <Column field="comment" header="Comment" style="width:15%">
                    <template #body="{data}">
                        <div v-html="getFormattedComment(data.comment)"></div>
                    </template>
                    <template #editor="{data, field}">
                        <Textarea v-model="data[field]" :autoResize="true"
                            placeholder="Comment" class="p-col-12" id="comment" />
                    </template>
                </Column>
                <Column field="last_run_at" header="Last Run" sortable dataType="date" class="p-text-center" style="width:15%; line-height: 1.25rem;">
                    <template #body="{data}">
                        <div v-html="getFormattedDate(data.last_run_at)" style="width:100%"></div>
                    </template>
                    <template #editor="{}"></template>
                </Column>
                <Column field="next_run_at" header="Next Run" sortable dataType="date" class="p-text-center" style="width:15%; line-height: 1.25rem;">
                    <template #body="{data}">
                        <div v-html="getFormattedDate(data.next_run_at)" style="width:100%"></div>
                    </template>
                    <template #editor="{data, field}">
                        <Calendar class="p-col-12" id="next_run_at" v-model="data[field]"
                            :showTime="true" :showSeconds="true" :minDate="new Date()" dateFormat="dd/mm/yy" />
                    </template>
                </Column>
                <Column field="last_updated_date" header="Last Update" sortable dataType="date" class="p-text-center" style="width:15%; line-height: 1.25rem;">
                    <template #body="{data}">
                        <div v-html="getFormattedDate(data.last_updated_date)" style="width:100%"></div>
                    </template>
                    <template #editor="{}"></template>
                </Column>
                <!-- <Column :rowEditor="false" style="width:5%" bodyStyle="text-align:right;"></Column> -->
                <Column header="" style="width:15%" bodyStyle="text-align:left;">
                    <template #body="item">
                        <div style="">
                            <Button label="" icon="pi pi-pencil" class="p-button-rounded p-button-text p-button-plain"
                                @click="showCronjobUpdateModal(item.data, item.index)" />

                            <Button label="" icon="pi pi-trash" class="p-button-rounded p-button-text p-button-plain"
                                @click="deleteCronjob(item.data.id)" />

                            <Button label="" icon="pi pi-eye" class="p-button-rounded p-button-text p-button-plain"
                                @click="showCronjobLogsModal(item.data)" />
                        </div>
                    </template>
                    <template #editor="{}"></template>
                </Column>
            </DataTable>
        </div>
    </div>

    <!-- cronjob edit modal -->
    <Dialog v-model:visible="updateCronjobForm.visible"
        :header="updateCronjobForm.cronjob ? updateCronjobForm.cronjob.function_name : ''"
        :modal="true" class="p-fluid p-col-6">
        <div class="p-grid">
            <div class="p-field p-col-12 p-md-6">
                <label for="function_name">Function Name</label>
                <InputText id="function_name" v-model="updateCronjobForm.cronjob.function_name" disabled />
            </div>

            <div class="p-field p-col-12 p-md-6">
                <label for="interval_in_minutes">Interval in Minutes</label>
                <InputNumber id="interval_in_minutes" v-model="updateCronjobForm.cronjob.interval_in_minutes" mode="decimal" :useGrouping="false" />
            </div>

            <div class="p-field p-col-12 p-md-6">
                <label for="is_enabled">Is Enabled</label> <br/>
                <Checkbox id="is_enabled" class="p-text-center"
                    v-model="updateCronjobForm.cronjob.enabled" :binary="true" :disabled="false" />
            </div>

            <div class="p-field p-col-12 p-md-6">
                <label for="is_running">Is Running</label> <br/>
                <Checkbox id="is_running" class="p-text-center"
                    v-model="updateCronjobForm.cronjob.is_running" :binary="true" />
            </div>

            <div class="p-field p-col-12 p-md-6">
                <label for="last_run_at">Last Run Date</label>
                <InputText id="last_run_at" :value="getFormattedDate(updateCronjobForm.cronjob.last_run_at)" :disabled="true" />
            </div>

            <div class="p-field p-col-12 p-md-6">
                <label for="last_updated_date">Last Update</label>
                <InputText id="last_updated_date" :value="getFormattedDate(updateCronjobForm.cronjob.last_updated_date)" :disabled="true" />
            </div>

            <div class="p-field p-col-12 p-md-6">
                <label for="next_run_at">Next Run</label>
                <Calendar class="p-col-12" id="next_run_at" v-model="updateCronjobForm.cronjob.next_run_at"
                    :showTime="true" :showSeconds="true" dateFormat="dd/mm/yy" hourFormat="HH:mm:ss" />
            </div>

            <div class="p-field p-col-12 p-md-12">
                <label for="comment">Comment</label>
                <Textarea v-model="updateCronjobForm.cronjob.comment" :autoResize="true"
                    placeholder="Comment" class="p-col-12" id="comment" />
            </div>
        </div>
        <template #footer>
            <Button label="Update Cronjob"
                @click="updateCronjob(updateCronjobForm.cronjob, updateCronjobForm.index)"
                class="p-button p-button-success" />
        </template>
    </Dialog>

    <!-- cronjob logs modal -->
    <Dialog v-model:visible="cronjobLogsModal.visible"
        :header="cronjobLogsModal.header" :modal="true" class="p-fluid p-col-9 p-text-center">
        <ProgressSpinner style="width:40px; height:40px" class="p-m-4"
            strokeWidth="4" fill="var(--surface-ground)" animationDuration="1.5s"
            v-show="cronjobLogsModal.loading" />

        <DataTable v-show="!cronjobLogsModal.loading" :value="cronjobLogsModal.items" dataKey="id" responsiveLayout="scroll"
            :paginator="true" :rows="25"
            :rowsPerPageOptions="[10, 25, 50, 100]">
            <template #empty>
                No log found for cronjob.
            </template>
            <Column field="id" header="#" style="width:5%"></Column>
            <Column header="Title/Logs" style="width:40%">
                <template #body="item">
                    <Button :label='item.data.title' 
                        :class="'p-text-light p-button-text p-button-succes p-text-left'"
                        @click="item.data.visible = (item.data.visible !== undefined ? !item.data.visible : true)" />

                    <div v-if="item.data.visible" class="p-mt-3">
                        <ul style="list-style:decimal">
                            <li v-for="(item, index) in item.data.logs"
                                :key="index">
                                <span v-html="item"></span>
                            </li>
                        </ul>
                    </div>
                </template>
            </Column>
            <Column header="Runtime (seconds)" style="width:10%">
                <template #body="item">
                    <p v-if="item.data.runtime_in_ms !== undefined && item.data.runtime_in_ms !== null">
                        {{item.data.runtime_in_ms / 1000}}
                    </p>
                </template>
            </Column>
            <Column field="log_date_str" header="Result" style="width:10%">
                <template #empty>
                    -
                </template>
                <template #body="item">
                    <div v-if="item.data.is_successful !== undefined">
                        <i v-if="item.data.is_successful" class="pi pi-check-circle" style="color:green"></i>
                        <i v-else class="pi pi-times-circle" style="color:red"></i>
                    </div>
                </template>
            </Column>
            <Column field="log_date_str" header="Log Date" style="width:35%"></Column>
        </DataTable>
    </Dialog>

    <Divider align="center" class="p-mt-6">
        <div class="p-d-inline-flex p-ai-center">
        <b>About</b>
        </div>
    </Divider>

    <div class="p-col-12 p-md-9 p-mx-auto p-mt-2 p-text-left">
        <div class="">
        <i class="fa fa-info p-mr-3"></i> Belirli aralıklarla otomatik işlemler gerçekleştirilmesi için hazırlanan cronjob'lar yönetilir.
        <br/> Yeni bir cronjob ekleneceği zaman <code>cronjob.model.js</code> içinde fonksiyon hazırlanır.
        <br/> Aynı dosya içindeki <code>validateForJobExecute</code> fonksiyonunda manuel çalıştırabilmek için yeni metodun tanımı eklenir.
        <br/> Force Run aktif edildikten sonra listeden job seçilerek Execute komutu verilebilir.
        <br/> <u><b>forceRun=true</b> çalışan job'ların bir sonraki çalışma zamanı bilgisi etkilenmez, yeniden güncellenmez.</u>
        <br/> Cronjob'lar çalışması sonlanırken kendisinin bir sonraki çalışma zamanını hesaplayarak cronjobs tablosunda günceller.
        <br/> Sonraki çalışma zamanı <code>next_run_at = next_run_at + interval_in_minutes</code> şeklinde hesaplanır.
        <br/> Geri kalmış bir cronjob, <i>Bree</i>'de tanımlı çok daha kısa aralıklarla çalışan <i>Cronjob processor</i> tarafından sürekli tetikleneceği için güncel zamanı yakalayacaktır.
        </div>
    </div>
</template>

<style>
.tag-disabled {
    background-color: #777;
    color:#BBB;
}

.tag-enabled {
    background-color: #9fdaa8
}
</style>

<script>
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import ProgressSpinner from 'primevue/progressspinner'
import Calendar from 'primevue/calendar'
import Dialog from 'primevue/dialog'
import ProgressBar from 'primevue/progressbar'
import Divider from 'primevue/divider'
import Tag from 'primevue/tag'

import { isEmpty } from '../utils/utils'
import Const from '../utils/const'
import moment from 'moment'
import dayjs from 'dayjs'

export default {
    name: 'CronjobManagement',
    emits: ["loginResult", "pageLoaded"],
    components: {
        InputText, InputNumber, Textarea, Button, DataTable, ProgressBar, Column, Calendar, Dialog, ProgressSpinner, Divider, Checkbox, Tag
    },
    data() {
        return {
            isNewCronjobFormVisible: false,
            newCronjobForm: {},
            isItemListLoading: false,
            cronjobList: [],
            updateCronjobForm: {
                cronjob: null,
                visible: false
            },
            cronjobLogsModal: {
                header: null,
                visible: false,
                loading: false,
                items: [],
                currentPage: 1,
                totalCount: 0,
                itemPerPage: 0,
                cronjobId: -1
            },
            selectionMode: null,
            selectedCronjobs: [],
            executingSelectedCronjobs: false
        }
    },
    beforeMount() {
        var page = {
            title: "Cronjob Management"
        }
        this.$emit('pageLoaded', page)
    },
    mounted() {
        this.loadCronjobs()
    },
    methods: {
        loadCronjobs() {
            let self = this,
                cookies = self.$cookies,
                toast = self.$toast,
                token = cookies.get('token');

            self.isItemListLoading = true
            self.$http.get('api/cronjobs', {
                headers: {
                    'authorization': 'Bearer ' + token
                }
            }).then(function (response) {
                self.isItemListLoading = false
                if(!isEmpty(response.data.kod) && response.data.kod === Const.KOD.basarili && !isEmpty(response.headers['refresh-token'])) {
                    cookies.set('token', response.headers['refresh-token']);
                    self.cronjobList = response.data.items
                } else if(response.data.kod === Const.KOD.oturumSonlandi) {
                    self.$router.push('/login')
                } else {
                    toast.add({severity:'warn', summary: 'Loading cronjobs failed', detail:response.data.mesaj, life: 3000});
                }
            }).catch(function (err) {
                self.isItemListLoading = false
                toast.add({severity:'error', summary: 'Error', detail:err});
                console.log(err)
            });
        },
        addCronjob() {
            let self = this,
                toast = self.$toast,
                newCronjobForm = self.newCronjobForm,
                toastMessage = null,
                cookies = self.$cookies,
                token = self.$cookies.get('token');

            if(isEmpty(newCronjobForm.function_name)) {
                toastMessage = 'Function Name is empty!'
            } else if(isEmpty(newCronjobForm.interval_in_minutes)) {
                toastMessage = 'Interval (Minutes) is empty!'
            }

            if(!isEmpty(toastMessage)) {
                toast.add({severity:'warn', summary: 'Something missing!', detail: toastMessage, life: 3000});
                return
            }

            self.$http.post('api/cronjobs', newCronjobForm, {
                headers: {
                    'authorization': 'Bearer ' + token
                }
            }).then(function (response) {
                if(!isEmpty(response.data.kod) && response.data.kod === Const.KOD.basarili && !isEmpty(response.headers['refresh-token'])) {
                    cookies.set('token', response.headers['refresh-token']);
                    let newAddedItem = response.data.item
                    
                    self.cronjobList.push(newAddedItem)

                    toast.add({severity:'success', summary: 'Success', detail: 'Cronjob added successfully.', life: 3000});

                    self.newCronjobForm = {}
                } else {
                    console.log(response)
                    toast.add({severity:'warn', summary: 'Cronjob insert failed', detail:response.data.mesaj, life: 3000});
                }
            }).catch(function (err) {
                console.log(err)
                toast.add({severity: 'error', summary: 'Error', detail: err});
            });
        },
        deleteCronjob(cronjobId) {
            let self = this,
                toast = self.$toast,
                cookies = self.$cookies,
                token = self.$cookies.get('token');
            
            this.$confirm.require({
                message: 'Are you sure you want to delete this cronjob permanently?',
                header: 'Delete',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Delete',
                rejectLabel: 'Cancel',
                accept: () => {
                    self.$http.delete('api/cronjobs/'+cronjobId, {
                    headers: {
                        'authorization': 'Bearer ' + token
                    }
                    }).then(function (response) {
                        if(!isEmpty(response.data.kod) && response.data.kod === Const.KOD.basarili && !isEmpty(response.headers['refresh-token'])) {
                            cookies.set("token", response.headers['refresh-token']);

                            self.loadCronjobs()

                            toast.add({severity:'success', summary: 'Success', detail: 'Cronjob deleted successfully.', life: 3000});
                        } else {
                            console.log(response)
                            toast.add({severity:'warn', summary: 'Cronjob delete failed', detail:response.data.mesaj, life: 3000});
                        }
                    }).catch(function (err) {
                        console.log(err)
                        toast.add({severity: 'error', summary: 'Error', detail: err});
                    });
                },
                reject: () => {
                }
            });
        },
        getFormattedDate(logDate) {
            return !isEmpty(logDate) ? moment(new Date(logDate)).format('DD/MM/YYYY HH:mm:ss') : "-";
        },
        getFormattedComment(comment) {
            return !isEmpty(comment) ? comment.replaceAll('\n', '<br/>') : null
        },
        onPage(event) {
            let self = this,
                newPage = event.page + 1
            
            self.cronjobLogsModal.currentPage = newPage
            self.getCronjobLogs()
        },
        showCronjobLogsModal(cronjob) {
            let self = this,
                cronjobLogsModal = self.cronjobLogsModal;
            
            // show and start loading
            cronjobLogsModal.header = cronjob.function_name + ' Logs'
            cronjobLogsModal.visible = true
            cronjobLogsModal.currentPage = 1
            cronjobLogsModal.cronjobId = cronjob.id
            
            self.getCronjobLogs()
        },
        getCronjobLogs() {
            let self = this,
                cronjobLogsModal = self.cronjobLogsModal,
                cronjobId = cronjobLogsModal.cronjobId,
                toast = self.$toast,
                cookies = self.$cookies,
                token = self.$cookies.get('token');
            
            cronjobLogsModal.loading = true
            self.$http.get('api/cronjobs/'+cronjobId+'/logs?page='+cronjobLogsModal.currentPage, {
                headers: {
                    'authorization': 'Bearer ' + token
                }
            }).then(function (response) {
                cronjobLogsModal.loading = false

                if(!isEmpty(response.data.kod) && response.data.kod === Const.KOD.basarili && !isEmpty(response.headers['refresh-token'])) {
                    cookies.set('token', response.headers['refresh-token']);
                    cronjobLogsModal.items = response.data.items
                    cronjobLogsModal.totalCount = response.data.totalCount
                    cronjobLogsModal.itemPerPage = response.data.itemPerPage
                } else if(response.data.kod === Const.KOD.oturumSonlandi) {
                    self.$router.push('/login')
                } else {
                    console.log(response.data)
                    toast.add({severity:'warn', summary: 'Get cronjob logs failed. Check console.', detail: response.data.mesaj, life: 3000});
                }
            }).catch(function (err) {
                cronjobLogsModal.loading = false
                toast.add({severity:'error', summary: 'Error', detail:err});
                console.log(err)
            });
        },
        executeSelectedCronjobs() {
            let self = this,
                toast = self.$toast,
                selectedCronjobs = self.selectedCronjobs,
                toastMessage = null,
                cookies = self.$cookies,
                token = self.$cookies.get('token');

            if(selectedCronjobs === undefined || selectedCronjobs === null || selectedCronjobs.length === 0) {
                toastMessage = 'Cronjob not selected!'
            }

            if(!isEmpty(toastMessage)) {
                toast.add({severity:'warn', summary: 'Something missing!', detail: toastMessage, life: 3000});
                return
            }

            var cronjobs = []
            selectedCronjobs.forEach(job => {
                cronjobs.push(job.function_name)
            })

            let executePayload = {
                jobs: cronjobs,
                forceRun: true
            }

            self.executingSelectedCronjobs = true
            self.$http.post('api/cronjobs/execute', executePayload, {
                headers: {
                    'authorization': 'Bearer ' + token
                }
            }).then(function (response) {
                self.executingSelectedCronjobs = false
                if(!isEmpty(response.data.kod) && response.data.kod === Const.KOD.basarili && !isEmpty(response.headers['refresh-token'])) {
                    cookies.set('token', response.headers['refresh-token']);
                    
                    toast.add({severity:'success', summary: 'Success', detail: 'Selected cronjobs executed successfully.', life: 3000});

                    self.selectedCronjobs = []
                    self.selectionMode = null
                    setTimeout(() => {
                        self.loadCronjobs()
                    }, 1000);
                } else {
                    console.log(response)
                    toast.add({severity:'warn', summary: 'Execute cronjobs failed', detail:response.data.mesaj});
                }
            }).catch(function (err) {
                self.executingSelectedCronjobs = false
                console.log(err)
                toast.add({severity: 'error', summary: 'Error', detail: err});
            });
        },
        showCronjobUpdateModal(cronjob, currentItemIndex) {
            let updateCronjobForm = this.updateCronjobForm

            updateCronjobForm.cronjob = cronjob
            updateCronjobForm.cronjob.next_run_at = dayjs(updateCronjobForm.cronjob.next_run_at, Const.DateFormats.DefaultTimestampFormat).toDate();
            updateCronjobForm.visible = true
            updateCronjobForm.index = currentItemIndex
        },
        async updateCronjob(newData, index) {
            let self = this,
                cronjobId = newData.id,
                newCronjob = {
                    interval_in_minutes: newData.interval_in_minutes,
                    comment: newData.comment,
                    next_run_at: newData.next_run_at,
                    enabled: newData.enabled
                },
                toast = self.$toast,
                toastMessage = null,
                cookies = self.$cookies,
                token = cookies.get('token');

            if(isEmpty(newCronjob.next_run_at)) {
                toastMessage = 'Next Run date can\'t be empty!'
            } else if(isEmpty(newCronjob.interval_in_minutes)) {
                toastMessage = 'Interval (Minutes) is empty!'
            }

            if(!isEmpty(toastMessage)) {
                toast.add({severity:'warn', summary: 'Something missing!', detail: toastMessage, life: 3000});
                return
            }

            try {
                let response = await self.$http.patch(`api/cronjobs/${cronjobId}`, newCronjob, {
                    headers: {
                        'authorization': 'Bearer ' + token
                    }
                })

                if(!isEmpty(response.data.kod) && response.data.kod === Const.KOD.basarili && !isEmpty(response.headers['refresh-token'])) {
                    cookies.set('token', response.headers['refresh-token']);
                    
                    self.cronjobList[index] = response.data.item;

                    toast.add({severity:'success', summary: 'Success', detail: 'Updated successfully.', life: 3000});

                    this.updateCronjobForm = {
                        cronjob: null,
                        visible: false
                    }
                } else {
                    toast.add({severity:'warn', summary: 'Update failed', detail:response.data.mesaj, life: 3000});
                }
            } catch(error) {
                console.log(error)
                toast.add({severity: 'error', summary: 'Error', detail: error});
            }
        }
    }
}
</script>
